import { Component, ViewChild, OnInit } from "@angular/core";
import {
  NavController,
  Events,
  NavParams,
  ModalController,
  AlertController
} from "ionic-angular";
import { Store } from "@ngrx/store";
import { AppState } from "../../app/models/app.state";
import { FhirService } from "../../app/services/fhir.service";
import { TranslateService } from "@ngx-translate/core";
import { DashboardService } from "../../app/services/dashboard.service";
import {
  DIAPROFIL_QUESTIONNAIRE_UUID,
  diaprofilTopics,
  manualItemTypes,
  manualItemTypeOptions,
  DiaprofilTopic
} from "../../app/models/types";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { WidgetConfig } from "@zitelab/ionic-fhir-questionnaires/dist/models/widget";
import { ManualItemsService } from "../../app/services/manual-items.service";
import { ManualItem, IManualItem } from "../../app/models/manual-item";
import { ManageManualItemModal } from "../../modals/manage-manual-item.modal/manage-manual-item.modal";
import { DomSanitizer } from "@angular/platform-browser";

@Component({
  selector: "manual-admin-page",
  templateUrl: "manual-admin.page.html"
})
export class ManualAdminPage implements OnInit {
  @ViewChild("manualItemsListing") table: any;
  localizedTableMessages: { [key: string]: string };
  manualItems: ManualItem[];
  diaprofilTopics = diaprofilTopics;
  manualItemTypeOptions = manualItemTypeOptions;
  tableFilters: {
    displayTitle: string;
    itemType: manualItemTypes;
    relevantTopics: DiaprofilTopic[];
  } = {
    displayTitle: null,
    itemType: null,
    relevantTopics: []
  };
  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public events: Events,
    public store: Store<AppState>,
    public fhir: FhirService,
    public manual: ManualItemsService,
    public dashboard: DashboardService,
    public translate: TranslateService,
    public modalCtrl: ModalController,
    public alertCtrl: AlertController,
    public sanitizer: DomSanitizer
  ) {
    this.store
      .select(state => state.manualItems)
      .subscribe(manualItems => {
        const allManualItems = manualItems.map(
          item => new ManualItem(item, translate, dashboard)
        );
        this.filterRows(this.tableFilters, allManualItems); // sets this.supportActions
      });
    this.localizedTableMessages = {
      // Message to show when array is presented
      // but contains no values
      emptyMessage: translate.instant(
        _i18n_str("MANUAL_ITEM_LISTING.MESSAGES.NO_DATA_TO_DISPLAY")
      ),
      // Footer total message
      totalMessage: translate.instant(
        _i18n_str("MANUAL_ITEM_LISTING.MESSAGES.TOTAL")
      )
    };
  }

  triggerManualItemModal($event, manualItem?: IManualItem) {
    if (this.dashboard.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.dashboard.setGenericProWidgets(questionnaireResource);
    }
    const navParams =
      manualItem !== undefined ? { manualItem, modalType: "EDIT" } : {};
    const modal = this.modalCtrl.create(ManageManualItemModal, navParams, {
      cssClass: "large-modal",
      enableBackdropDismiss: false
    });
    modal.present();
  }

  triggerDeleteManualItemAlert(event, manualItem: IManualItem) {
    const prompt = this.alertCtrl.create({
      title: this.translate.instant(
        "MANUAL_ADMIN_PAGE.DELETE_MANUAL_ITEM_ALERT.HEADER"
      ),
      message: this.translate.instant(
        "MANUAL_ADMIN_PAGE.DELETE_MANUAL_ITEM_ALERT.MESSAGE"
      ),
      buttons: [
        {
          text: this.translate.instant(
            "MANUAL_ADMIN_PAGE.DELETE_MANUAL_ITEM_ALERT.OK_BTN"
          ),
          handler: data => {
            this.manual.deleteManualItem(manualItem);
          }
        },
        {
          text: this.translate.instant(
            "MANUAL_ADMIN_PAGE.DELETE_MANUAL_ITEM_ALERT.CANCEL_BTN"
          ),
          handler: data => {
            //
          }
        }
      ]
    });
    prompt.present();
  }

  ngOnInit() {
    if (this.dashboard.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.dashboard.setGenericProWidgets(questionnaireResource);
    }
  }

  selectProItemFilter(term: string, item: WidgetConfig<any>) {
    if (!item) {
      return false;
    }
    return `${item.prefix} ${item.label}`.indexOf(term) > -1;
  }

  getQuestionConfigBasedOnId(questionId: string): WidgetConfig<any> {
    if (this.dashboard.widgets === null) {
      return null;
    }
    const questionWidget = this.dashboard.widgets.find(
      widget => widget.name === questionId
    );
    return questionWidget !== undefined ? questionWidget : null;
  }

  ionViewWillEnter() {
    this.manual.getManualItems();
  }

  onDetailToggle(event) {
    console.log("Detail Toggled", event);
  }

  toggleExpandRow(row) {
    this.table.rowDetail.toggleExpandRow(row);
  }
  filterRows(filterValues, allManualItems = this.manual.manualItems) {
    const hasIntersection = (a: string[], b: string[]) => {
      if (a === null || b === null) {
        return false;
      }
      const setA = new Set(a);
      const setB = new Set(b);
      return [...setA].filter(id => setB.has(id)).length > 0;
    };
    const filters = {
      displayTitle: (item: ManualItem, filterBy: string) => {
        return (
          item.displayTitle
            .toLocaleLowerCase()
            .indexOf(filterBy.toLocaleLowerCase()) > -1
        );
      },
      itemType: (item: ManualItem, filterBy: string) =>
        filterBy === "ALL" || item.itemType === filterBy,
      relevantTopics: (item: ManualItem, filterBy: string[]) =>
        hasIntersection(item.relevantTopics, filterBy)
    };
    const activeFilters = Object.keys(filters).filter(
      (keyToFilterOn, index) => {
        const filterValue = filterValues[keyToFilterOn];
        return (
          filterValue !== null &&
          filterValue !== "ALL" &&
          !(Array.isArray(filterValue) && filterValue.length === 0)
        );
      }
    );
    const runFilters = (item: ManualItem) => {
      let result = true;
      for (const filter of activeFilters) {
        const filterFunction = filters[filter];
        const filterBy = filterValues[filter];
        const filterResult = filterFunction(item, filterBy);
        if (filterResult === false) {
          result = false;
          break;
        }
      }
      return result;
    };
    const filteredManualItems = allManualItems.filter(item => runFilters(item));
    this.manualItems = filteredManualItems;
  }
}



// WEBPACK FOOTER //
// ./src/pages/manual-admin.page/manual-admin.page.ts